const CARET_ICON =
  '<span class="svg-wrapper icon-caret icon-animated"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M9.303.397a1 1 0 0 1 0 1.414L5.586 5.53a1 1 0 0 1-1.414 0L.455 1.81A1 1 0 1 1 1.869.397L4.879 3.4 9.303.397Z" clip-rule="evenodd"/></svg></span>';

/**
 * @param {Element | null | undefined} node
 */
function isFaqDividerNode(node) {
  if (!(node instanceof HTMLElement)) return false;

  if (node.dataset.type === 'divider') return true;
  if (node.tagName === 'HR') return true;

  return node.matches('div:has(> hr:only-child), div:has(> hr:first-child:last-child)');
}

/**
 * @param {HTMLElement} root
 */
function removeFaqDividers(root) {
  root.querySelectorAll('[data-type="divider"]').forEach((divider) => divider.remove());
  root.querySelectorAll('.faq-page__answer hr').forEach((hr) => hr.remove());
}

/**
 * @param {HTMLElement} root
 */
function buildAccordionFromPageContent(root) {
  if (root.dataset.breakrealFaqBuilt === 'true') return;

  const headings = root.querySelectorAll('h2, h3, h4');

  if (!headings.length) return;

  /** @type {DocumentFragment} */
  const fragment = document.createDocumentFragment();

  headings.forEach((heading, index) => {
    const item = document.createElement('div');
    item.className = 'faq-page__item';
    if (index === 0) item.classList.add('is-open');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'faq-page__trigger button-unstyled';
    trigger.setAttribute('aria-expanded', index === 0 ? 'true' : 'false');

    const question = document.createElement('span');
    question.className = 'faq-page__question';
    question.textContent = heading.textContent?.trim() ?? '';
    trigger.append(question);
    trigger.insertAdjacentHTML('beforeend', CARET_ICON);

    const panel = document.createElement('div');
    panel.className = 'faq-page__panel';

    const panelInner = document.createElement('div');
    panelInner.className = 'faq-page__panel-inner';

    const answer = document.createElement('div');
    answer.className = 'faq-page__answer rte';

    let node = heading.nextElementSibling;

    while (node && !/^H[2-4]$/.test(node.tagName)) {
      const next = node.nextElementSibling;

      if (!isFaqDividerNode(node)) {
        answer.append(node);
      }

      node = next;
    }

    removeFaqDividers(answer);
    panelInner.append(answer);
    panel.append(panelInner);
    item.append(trigger, panel);
    fragment.append(item);
  });

  root.replaceChildren(fragment);
  root.dataset.breakrealFaqBuilt = 'true';
}

/**
 * @param {HTMLElement} accordion
 */
function ensureFirstItemOpen(accordion) {
  const items = accordion.querySelectorAll('.faq-page__item');
  if (!items.length) return;

  items.forEach((item, index) => {
    if (!(item instanceof HTMLElement)) return;

    const shouldOpen = index === 0;
    const trigger = item.querySelector('.faq-page__trigger');
    const panel = item.querySelector('.faq-page__panel');

    item.classList.toggle('is-open', shouldOpen);
    trigger?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    panel?.removeAttribute('hidden');
  });
}

/**
 * @param {HTMLElement} accordion
 */
function bindAccordion(accordion) {
  if (accordion.dataset.breakrealFaqBound === 'true') return;

  accordion.addEventListener('click', (event) => {
    const trigger = event.target.closest('.faq-page__trigger');
    if (!(trigger instanceof HTMLButtonElement)) return;

    const item = trigger.closest('.faq-page__item');
    if (!(item instanceof HTMLElement)) return;

    const isOpen = item.classList.contains('is-open');

    accordion.querySelectorAll('.faq-page__item.is-open').forEach((openItem) => {
      if (openItem === item) return;
      openItem.classList.remove('is-open');
      openItem.querySelector('.faq-page__trigger')?.setAttribute('aria-expanded', 'false');
    });

    item.classList.toggle('is-open', !isOpen);
    trigger.setAttribute('aria-expanded', String(!isOpen));
  });

  accordion.dataset.breakrealFaqBound = 'true';
}

function initBreakrealFaqAccordions() {
  document.querySelectorAll('[data-breakreal-faq-accordion]').forEach((accordion) => {
    if (!(accordion instanceof HTMLElement)) return;

    if (accordion.dataset.breakrealFaqSource === 'page-content') {
      buildAccordionFromPageContent(accordion);
    } else {
      removeFaqDividers(accordion);
      accordion.dataset.breakrealFaqBuilt = 'true';
    }

    bindAccordion(accordion);
    ensureFirstItemOpen(accordion);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBreakrealFaqAccordions);
} else {
  initBreakrealFaqAccordions();
}

document.addEventListener('shopify:section:load', initBreakrealFaqAccordions);
